<template>
  <div class="sd-card border-l-4 border-l-securdia-blue -mx-4 sm:-mx-8 lg:-mx-16">
    <!-- Header Card -->
    <div class="p-4 border-b border-secondary-100">
      <div class="flex flex-col lg:flex-row lg:items-end gap-4">
        <!-- TB Calculator Title -->
        <div class="flex items-center gap-3 flex-shrink-0">
          <div class="w-10 h-10 bg-securdia-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg class="w-5 h-5 text-securdia-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
            </svg>
          </div>
          <div>
            <h2 class="text-base font-bold text-securdia-blue">{{ $t('sales_dashboard.tb_calculator_title') }}</h2>
            <p class="text-xs text-secondary-500">{{ $t('sales_dashboard.tb_calculator_description') }}</p>
          </div>
        </div>

        <!-- Agreement Section -->
        <div v-if="activeSellerAgreements.length > 0" class="flex flex-col gap-2">
          <h3 class="text-xs font-semibold text-secondary-700 uppercase tracking-wide">{{ $t('agreement') }}</h3>
          <div class="flex flex-wrap items-end gap-2">
            <div>
              <select v-model="selectedAgreement" class="sd-select-sm min-w-[180px]">
                <option :value="null">{{ $t('sales_dashboard.select_agreement_placeholder') }}</option>
                <option v-for="agreement in activeSellerAgreements" :key="agreement._id" :value="agreement.agreement_number">
                  {{ agreement.agreement_number }}
                </option>
              </select>
            </div>
            <button 
              @click="loadAgreementToCalculator"
              :disabled="!selectedAgreement"
              class="px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap transition-colors"
            >
              {{ $t('sales_dashboard.calculate_tb') }}
            </button>
          </div>
        </div>

        <!-- Offers Section -->
        <div class="flex flex-col gap-2">
          <h3 class="text-xs font-semibold text-secondary-700 uppercase tracking-wide">{{ $t('sales_dashboard.offers_tab') }}</h3>
          <div class="flex flex-wrap items-end gap-2">
            <div>
              <select v-model="selectedOffer" class="sd-select-sm min-w-[180px]">
                <option :value="null">{{ $t('sales_dashboard.select_offer_placeholder') }}</option>
                <option v-for="offer in sellerOffers" :key="offer._id" :value="offer._id">
                  {{ offer.name }}{{ offer.seller_id !== activeSellerID ? ` (${$t('sales_dashboard.shared_badge')})` : '' }}
                </option>
              </select>
            </div>
            <button 
              @click="loadOfferToCalculator"
              :disabled="!selectedOffer"
              class="px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap transition-colors"
            >
              {{ $t('sales_dashboard.import_offer') }}
            </button>
            <button 
              @click="openSaveOfferModal"
              :disabled="calculatorProducts.length === 0"
              class="px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-1 transition-colors"
            >
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path>
              </svg>
              {{ $t('sales_dashboard.save_offer') }}
            </button>
            <button 
              v-if="selectedOffer"
              @click="deleteSelectedOffer"
              class="px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded-md hover:bg-red-600 whitespace-nowrap flex items-center justify-center gap-1 transition-colors"
            >
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
              {{ $t('sales_dashboard.delete_offer') }}
            </button>
            <button 
              v-if="selectedOffer"
              @click="exportSelectedOffer"
              :disabled="exportingPdf"
              class="px-3 py-1.5 bg-gray-500 text-white text-xs font-medium rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-1 transition-colors"
            >
              <svg v-if="exportingPdf" class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              <svg v-else class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              {{ exportingPdf ? $t('sales_dashboard.exporting') : $t('sales_dashboard.export_offer') }}
            </button>
            <button 
              v-if="selectedOffer && isOfferOwner"
              @click="openShareModal"
              class="px-3 py-1.5 bg-blue-500 text-white text-xs font-medium rounded-md hover:bg-blue-600 whitespace-nowrap flex items-center justify-center gap-1 transition-colors"
            >
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path>
              </svg>
              {{ $t('sales_dashboard.share_offer') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Calculator Grid -->
    <div class="p-4">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:flex xl:flex-wrap 2xl:flex-nowrap gap-3 2xl:h-[680px]">
        <!-- 1. Settings Panel (far left) -->
    <div class="calc-panel-settings bg-white rounded-lg shadow-md p-3 space-y-4 flex flex-col xl:min-h-0 relative">
      <!-- Lock overlay when agreement/offer is loaded -->
      <div v-if="isItemLoaded" class="absolute inset-0 bg-white bg-opacity-50 z-[5] rounded-lg cursor-not-allowed group" :title="$t('sales_dashboard.locked_panel_hint')">
        <div class="absolute top-0 left-0 right-0 bottom-[180px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <span class="bg-secondary-800 text-white text-[10px] px-3 py-1.5 rounded shadow-lg text-center max-w-[160px] leading-tight">{{ $t('sales_dashboard.locked_panel_hint') }}</span>
        </div>
      </div>
      <h4 class="font-semibold text-secondary-700 text-sm border-b border-secondary-200 pb-2 flex-shrink-0">{{ $t('sales_dashboard.contract_settings') }}</h4>
        
        <!-- Larmcentral -->
        <div>
          <label class="block text-xs font-medium text-secondary-600 mb-1">{{ $t('sales_dashboard.alarm_center') }}</label>
          <select 
            v-model="selectedAlarmCenter"
            class="sd-select-sm w-full"
          >
            <option 
              v-for="ac in alarmCenterOptions" 
              :key="ac.value" 
              :value="ac.value"
            >
              {{ ac.label }}
            </option>
          </select>
        </div>
        
        <!-- Simkort -->
        <div>
          <label class="block text-xs font-medium text-secondary-600 mb-1">{{ $t('sales_dashboard.simcard') }}</label>
          <select 
            v-model="selectedSimcard"
            class="sd-select-sm w-full"
          >
            <option 
              v-for="sim in simcardOptions" 
              :key="sim.value" 
              :value="sim.value"
            >
              {{ sim.label }}
            </option>
          </select>
        </div>
        
        <!-- Avtalsperiod -->
        <div>
          <label class="block text-xs font-medium text-secondary-600 mb-1">{{ $t('sales_dashboard.contract_period_label') }}</label>
          <select 
            v-model.number="contractPeriod"
            class="sd-select-sm w-full"
            :disabled="isPureCashPurchase"
            :class="{ 'opacity-60 cursor-not-allowed': isPureCashPurchase }"
            :title="isPureCashPurchase ? $t('sales_dashboard.cash_purchase_tooltip') : ''"
          >
            <option :value="12">12 {{ $t('sales_dashboard.months') }} (1 {{ $t('sales_dashboard.year_suffix') }})</option>
            <option :value="24">24 {{ $t('sales_dashboard.months') }} (2 {{ $t('sales_dashboard.year_suffix') }})</option>
            <option :value="36">36 {{ $t('sales_dashboard.months') }} (3 {{ $t('sales_dashboard.year_suffix') }})</option>
            <option :value="48">48 {{ $t('sales_dashboard.months') }} (4 {{ $t('sales_dashboard.year_suffix') }})</option>
            <option :value="60">60 {{ $t('sales_dashboard.months') }} (5 {{ $t('sales_dashboard.year_suffix') }})</option>
            <option :value="72">72 {{ $t('sales_dashboard.months') }} (6 {{ $t('sales_dashboard.year_suffix') }})</option>
            <option :value="84">84 {{ $t('sales_dashboard.months') }} (7 {{ $t('sales_dashboard.year_suffix') }})</option>
            <option :value="96">96 {{ $t('sales_dashboard.months') }} (8 {{ $t('sales_dashboard.year_suffix') }})</option>
            <option :value="108">108 {{ $t('sales_dashboard.months') }} (9 {{ $t('sales_dashboard.year_suffix') }})</option>
            <option :value="120">120 {{ $t('sales_dashboard.months') }} (10 {{ $t('sales_dashboard.year_suffix') }})</option>
          </select>
          <p v-if="isPureCashPurchase" class="text-[9px] text-amber-600 mt-0.5">{{ $t('sales_dashboard.cash_purchase_1_year') }}</p>
        </div>
    
        <!-- Övrigt engångskostnad -->
        <div>
          <label class="block text-xs font-medium text-secondary-600 mb-1">{{ $t('sales_dashboard.extra_fees') }}</label>
          <input 
            v-model.number="otherCost"
            type="number"
            min="0"
            class="sd-select-sm w-full"
            placeholder="0"
          />
        </div>
        
        <button 
          @click="reset"
          class="w-full px-2 py-1.5 text-xs bg-gray-500 text-white rounded-md hover:bg-gray-600 flex items-center justify-center gap-1 relative z-10"
        >
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          {{ $t('sales_dashboard.reset_btn') }}
        </button>
        
        <!-- Cost Summary (always visible) -->
        <div class="pt-3 border-t border-secondary-200 space-y-2 mt-auto">
          <h5 class="font-semibold text-secondary-700 text-[10px] uppercase tracking-wide">{{ $t('sales_dashboard.monthly_cost_label') }}</h5>
          
          <!-- Larmcentral cost -->
          <div class="flex justify-between items-center text-xs">
            <span class="text-secondary-600">{{ $t('sales_dashboard.alarm_center') }}:</span>
            <span class="font-medium text-secondary-800">{{ alarmCenterQuantity }} × {{ selectedAlarmCenterCost }} kr</span>
          </div>
          <div class="text-right text-xs font-semibold text-secondary-700">= {{ formatCurrency(alarmCenterTotalCost) }}</div>
          
          <!-- Simcard cost -->
          <div class="flex justify-between items-center text-xs mt-2">
            <span class="text-secondary-600">{{ $t('sales_dashboard.simcard') }}:</span>
            <span class="font-medium text-secondary-800">{{ simcardQuantity }} × {{ selectedSimcardCost }} kr</span>
          </div>
          <div class="text-right text-xs font-semibold text-secondary-700">= {{ formatCurrency(simcardTotalCost) }}</div>
          
          <!-- Total services cost -->
          <div class="pt-2 border-t border-secondary-200 flex justify-between items-center">
            <span class="text-xs font-semibold text-secondary-700">{{ $t('sales_dashboard.total_services') }}:</span>
            <span class="text-sm font-bold text-securdia-blue">{{ formatCurrency(servicesTotalCost) }}{{ $t('sales_dashboard.per_month_short') }}</span>
          </div>
        </div>
    </div>

    <!-- 2. Product Selection (dropdowns + add) -->
    <div class="calc-panel-product-select bg-white rounded-lg shadow-md p-3 space-y-3 flex flex-col xl:min-h-0 relative">
      <!-- Lock overlay when agreement/offer is loaded -->
      <div v-if="isItemLoaded" class="absolute inset-0 bg-white bg-opacity-50 z-[5] rounded-lg cursor-not-allowed group" :title="$t('sales_dashboard.locked_panel_hint')">
        <div class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <span class="bg-secondary-800 text-white text-[10px] px-3 py-1.5 rounded shadow-lg text-center max-w-[160px] leading-tight">{{ $t('sales_dashboard.locked_panel_hint') }}</span>
        </div>
      </div>
      <h4 class="font-semibold text-secondary-700 text-sm border-b border-secondary-200 pb-2 flex-shrink-0">{{ $t('sales_dashboard.add_product_title') }}</h4>
        
        <!-- Category Filter -->
        <div>
          <label class="block text-xs font-medium text-secondary-600 mb-1">{{ $t('sales_dashboard.product_category') }}</label>
          <select 
            v-model="selectedCategory"
            class="sd-select-sm w-full"
            @change="selectedSubcategory = ''"
          >
            <option value="">{{ $t('sales_dashboard.filter_all_categories') }}</option>
            <option 
              v-for="cat in categoryOptions" 
              :key="cat.value" 
              :value="cat.value"
            >
              {{ cat.label }}
            </option>
          </select>
        </div>

        <!-- Subcategory Filter -->
        <div>
          <label class="block text-xs font-medium text-secondary-600 mb-1">{{ $t('products.subcategory') }}</label>
          <select 
            v-model="selectedSubcategory"
            class="sd-select-sm w-full"
            :disabled="subcategoryOptions.length <= 1"
            :class="{ 'opacity-60': subcategoryOptions.length <= 1 }"
          >
            <option 
              v-for="sub in subcategoryOptions" 
              :key="sub.value" 
              :value="sub.value"
            >
              {{ sub.label }}
            </option>
          </select>
        </div>

        <!-- Product dropdown -->
        <div>
          <label class="block text-xs font-medium text-secondary-600 mb-1">{{ $t('sales_dashboard.select_product') }}</label>
          <select 
            v-model="selectedProductId"
            class="sd-select-sm w-full"
          >
            <option value="">{{ $t('sales_dashboard.select_product') }}</option>
            <option 
              v-for="item in selectableProducts" 
              :key="item.id" 
              :value="item.id"
            >
              {{ item.name }}
            </option>
          </select>
        </div>

        <!-- Add button - right under product dropdown -->
        <button 
          @click="addProduct"
          :disabled="!selectedProductId"
          class="w-full px-3 py-2 bg-green-500 text-white text-sm rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
          {{ $t('sales_dashboard.add_btn') }}
        </button>

        <!-- Spacer to push startavgifter down -->
        <div class="flex-1"></div>

        <!-- Startavgifter - at bottom -->
        <div v-if="personlarmCount > 0" class="pt-3 border-t border-secondary-200 space-y-2">
          <h5 class="font-semibold text-secondary-700 text-[10px] uppercase tracking-wide">{{ $t('sales_dashboard.startup_fees') }}</h5>
          <div class="flex justify-between items-center text-xs">
            <span class="text-secondary-600">{{ $t('sales_dashboard.count_label') }}:</span>
            <span class="font-medium text-secondary-800">{{ personlarmCount }} {{ $t('sales_dashboard.pieces_unit') }}</span>
          </div>
          <div class="flex justify-between items-center text-xs">
            <span class="text-secondary-600">{{ $t('sales_dashboard.startup_fee_per_unit') }}:</span>
            <span class="font-bold text-red-600">{{ formatCurrency(sosAppTotalCost) }}</span>
          </div>
        </div>

    </div>

    <!-- 3. Products List (middle, flexible) -->
    <div class="calc-panel-product-list bg-white rounded-lg shadow-md p-3 flex flex-col xl:min-h-0 overflow-hidden relative md:col-span-2 lg:col-span-1">
      <!-- Lock indicator when agreement/offer is loaded -->
      <div v-if="isItemLoaded" class="absolute top-2 right-2 z-10" :title="$t('sales_dashboard.locked_panel_hint')">
        <svg class="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
        </svg>
      </div>
      <h4 class="font-semibold text-secondary-700 text-sm border-b border-secondary-200 pb-2 mb-3 flex-shrink-0">{{ $t('sales_dashboard.added_products') }}</h4>
      
      <div v-if="calculatorProducts.length > 0" class="space-y-2 flex-1 pr-1" style="overflow-y: auto; max-height: 450px; min-height: 0;">
          <div 
            v-for="(item, index) in calculatorProducts" 
            :key="index"
            class="p-1.5 bg-secondary-50 rounded border border-secondary-200"
          >
            <div class="flex items-start justify-between mb-1">
              <div class="flex items-center gap-2">
                <span class="font-semibold text-secondary-800 text-sm leading-tight">{{ item.name }}</span>
                <!-- Cash/Rental Toggle - Both options visible -->
                <div v-if="isHardwareCategory(item.category)" class="flex rounded overflow-hidden text-[9px] font-medium">
                  <button 
                    @click="setCashPurchase(index, false)"
                    :disabled="isAgreementLoaded"
                    class="px-1.5 py-0.5 transition-colors"
                    :class="!item.isCashPurchase 
                      ? 'bg-secondary-700 text-white border border-secondary-800' 
                      : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200 border border-secondary-300'"
                  >
                    {{ $t('sales_dashboard.rental_button') }}
                  </button>
                  <button 
                    @click="setCashPurchase(index, true)"
                    :disabled="isAgreementLoaded"
                    class="px-1.5 py-0.5 transition-colors"
                    :class="item.isCashPurchase 
                      ? 'bg-secondary-700 text-white border border-secondary-800' 
                      : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200 border border-secondary-300'"
                  >
                    {{ $t('sales_dashboard.cash_button') }}
                  </button>
                </div>
              </div>
              <button 
                v-if="!isAgreementLoaded"
                @click="removeProduct(index)"
                class="bg-red-500 text-white hover:bg-red-600 ml-1 p-1 rounded"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            <div class="grid grid-cols-4 gap-1 text-[10px]">
              <div>
                <label class="block text-secondary-400 mb-0.5 text-[8px]">{{ $t('sales_dashboard.quantity_short') }}</label>
                <input 
                  type="number" 
                  v-model.number="calculatorProducts[index].quantity"
                  min="1"
                  :readonly="isAgreementLoaded"
                  class="w-full px-1 py-0.5 border rounded text-[10px]"
                  :class="isAgreementLoaded ? 'border-secondary-200 bg-secondary-50 text-secondary-600 cursor-not-allowed' : 'border-secondary-300 focus:ring-1 focus:ring-securdia-blue'"
                >
              </div>
              
              <div>
                <label class="block text-secondary-400 mb-0.5 text-[8px]">{{ item.isCashPurchase ? 'Pris/st' : $t('sales_dashboard.price_per_month_short') }}</label>
                <input 
                  type="number" 
                  v-model.number="calculatorProducts[index].salePrice"
                  min="0"
                  :readonly="isAgreementLoaded"
                  class="w-full px-1 py-0.5 border rounded text-[10px]"
                  :class="isAgreementLoaded ? 'border-secondary-200 bg-secondary-50 text-secondary-600 cursor-not-allowed' : (item.isCashPurchase ? 'border-secondary-300 bg-green-50 focus:ring-1 focus:ring-securdia-blue' : 'border-secondary-300 focus:ring-1 focus:ring-securdia-blue')"
                >
              </div>
              
              <div>
                <label class="block text-secondary-400 mb-0.5 text-[8px]">{{ $t('sales_dashboard.purchase_per_unit') }}</label>
                <input 
                  type="text" 
                  :value="formatCurrency(getItemPurchasePrice(item))"
                  readonly
                  class="w-full px-1 py-0.5 border border-secondary-200 rounded text-[10px] bg-secondary-50 text-secondary-600 cursor-not-allowed"
                >
              </div>
              
              <div>
                <label class="block text-secondary-400 mb-0.5 text-[8px]">{{ $t('sales_dashboard.total_purchase') }}</label>
                <input 
                  type="text" 
                  :value="formatCurrency(getItemPurchasePrice(item) * item.quantity)"
                  readonly
                  class="w-full px-1 py-0.5 border border-secondary-200 rounded text-[10px] bg-secondary-50 text-secondary-600 cursor-not-allowed"
                >
              </div>
            </div>
            
            <div class="flex justify-between items-center mt-1 pt-1 border-t border-secondary-200 text-[9px]">
              <span class="text-secondary-600">
                {{ formatCurrency(enrichedProducts[index].lineTotal) }}
                <span v-if="!item.isCashPurchase">({{ contractPeriod }} {{ $t('sales_dashboard.months_short') }})</span>
                <span v-else class="text-green-600 font-medium">({{ $t('sales_dashboard.cash_short') }})</span>
              </span>
              <span :class="getMarginClass(enrichedProducts[index].marginPercent)" class="font-bold">
                TB: {{ enrichedProducts[index].marginPercent.toFixed(0) }}%
              </span>
            </div>
          </div>
        </div>

        <!-- Empty state -->
      <div v-else class="text-center py-8 text-secondary-400 flex-1 flex flex-col justify-center">
        <svg class="w-10 h-10 mx-auto mb-2 text-secondary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
        </svg>
        <p class="text-xs">{{ $t('sales_dashboard.add_products_to_calculate') }}</p>
      </div>
    </div>

    <!-- 4. Results (right side) -->
    <div class="calc-panel-results bg-white rounded-lg shadow-md p-3 flex flex-col xl:min-h-0">
      <h4 class="font-semibold text-secondary-700 text-sm border-b border-secondary-200 pb-2 flex-shrink-0">{{ $t('sales_dashboard.results') }}</h4>
        
        <template v-if="calculatorProducts.length > 0">
          <div class="space-y-4 flex-1 mt-3">
            <!-- Main TB Display -->
            <div :class="getTBBackgroundClass(totalMarginPercent)" class="p-4 rounded-xl text-white">
              <div class="text-center">
                <p :class="getTBSubtextClass(totalMarginPercent)" class="text-xs mb-1">{{ $t('sales_dashboard.gross_margin') }}</p>
                <div class="text-3xl font-bold text-black">{{ totalMarginPercent.toFixed(1) }}%</div>
                <p :class="getTBSubtextClass(totalMarginPercent)" class="text-lg font-semibold mt-1">{{ formatCurrency(totalProfit) }}</p>
              </div>
            </div>

            <!-- Revenue/Cost Summary -->
            <div class="grid grid-cols-2 gap-2">
              <div class="p-3 bg-green-50 rounded-lg text-center border border-green-200">
                <p class="text-sm text-green-600 mb-1">{{ $t('sales_dashboard.total_revenue') }}</p>
                <p class="text-base font-bold text-green-700">{{ formatCurrency(totalRevenue) }}</p>
              </div>
              <div class="p-3 bg-red-50 rounded-lg text-center border border-red-200">
                <p class="text-sm text-red-600 mb-1">{{ $t('sales_dashboard.total_cost') }}</p>
                <p class="text-base font-bold text-red-700">{{ formatCurrency(totalCost) }}</p>
              </div>
            </div>

            <!-- Cost Breakdown -->
            <div class="p-2 bg-gray-50 rounded-lg border border-gray-200">
              <h5 class="font-medium text-gray-700 mb-1.5 text-[10px]">{{ $t('sales_dashboard.cost_breakdown') }}</h5>
              <div class="space-y-1">
                <div v-if="fixedCosts.startupFees > 0" class="flex justify-between items-center text-[10px] text-gray-600">
                  <span>{{ $t('sales_dashboard.startup_fees') }}:</span>
                  <span>{{ formatCurrency(fixedCosts.startupFees) }}</span>
                </div>
                <div v-if="fixedCosts.objectValue + fixedCosts.cashPurchaseCost > 0" class="flex justify-between items-center text-[10px] text-gray-600">
                  <span>{{ $t('sales_dashboard.hardware_cost') }}:</span>
                  <span>{{ formatCurrency(fixedCosts.objectValue + fixedCosts.cashPurchaseCost) }}</span>
                </div>
                <div v-if="fixedCosts.total > 0" class="flex justify-between items-center text-[10px] font-semibold text-gray-700 pt-1 border-t border-gray-200">
                  <span>{{ $t('sales_dashboard.fixed_costs') }}:</span>
                  <span>{{ formatCurrency(fixedCosts.total) }}</span>
                </div>
                <div v-if="variableCosts.total > 0" class="flex justify-between items-center text-[10px] font-semibold text-gray-700">
                  <span>{{ $t('sales_dashboard.variable_costs') }}:</span>
                  <span>{{ formatCurrency(variableCosts.total) }}</span>
                </div>
              </div>
            </div>
            
            <!-- Cash Sales Summary (if any) -->
            <div v-if="hasCashPurchases" class="p-2 bg-green-50 rounded-lg border border-green-200">
              <h5 class="font-medium text-green-700 mb-1 text-[10px]">{{ $t('sales_dashboard.cash_sale_summary') }}</h5>
              <div class="space-y-1 text-[10px]">
                <div class="flex justify-between">
                  <span class="text-green-600">{{ $t('sales_dashboard.revenue_short') }}:</span>
                  <span class="font-semibold text-green-800">{{ formatCurrency(totalCashPurchaseRevenue) }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-green-600">{{ $t('sales_dashboard.cost_short') }}:</span>
                  <span class="font-semibold text-green-800">{{ formatCurrency(totalCashPurchaseCost) }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-green-600">TB:</span>
                  <span class="font-semibold text-green-800">{{ formatCurrency(totalCashPurchaseRevenue - totalCashPurchaseCost) }}</span>
                </div>
              </div>
            </div>

            <!-- Monthly Flow -->
            <div class="p-2 bg-blue-50 rounded-lg border border-blue-200">
              <h5 class="font-medium text-blue-700 mb-1 text-[10px]">{{ $t('sales_dashboard.monthly_flow') }}</h5>
              <div class="space-y-1 text-[10px]">
                <div class="flex justify-between">
                  <span class="text-blue-600">{{ $t('sales_dashboard.monthly_revenue') }}:</span>
                  <span class="font-semibold text-blue-800">{{ formatCurrency(monthlyRevenue) }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-blue-600">{{ $t('sales_dashboard.monthly_cost') }}:</span>
                  <span class="font-semibold text-blue-800">{{ formatCurrency(monthlyCost) }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-blue-600">{{ $t('sales_dashboard.monthly_margin') }}:</span>
                  <span class="font-semibold text-green-700">{{ formatCurrency(monthlyMargin) }}</span>
                </div>
              </div>
            </div>

            <!-- Break-even Analysis (only show if no cash purchases) -->
            <div v-if="breakEvenMonths > 0 && !hasCashPurchases" class="p-2 bg-purple-50 rounded-lg border border-purple-200">
              <h5 class="font-medium text-purple-700 mb-1 text-[10px] flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                </svg>
                {{ $t('sales_dashboard.break_even_analysis') }}
              </h5>
              <p class="text-xs font-bold text-purple-700 text-center">{{ $t('sales_dashboard.break_even_after', { months: breakEvenMonths }) }}</p>
            </div>
          </div>
        </template>

        <!-- Empty results state -->
      <div v-else class="text-center py-6 text-secondary-400 flex-1 flex flex-col justify-center">
        <p class="text-xs">{{ $t('sales_dashboard.add_products_for_results') }}</p>
      </div>
    </div>

    <!-- 5. Commission/Provision + Umeå Finans (far right) -->
    <div class="calc-panel-commission bg-white rounded-lg shadow-md p-3 flex flex-col xl:min-h-0">
      <!-- Provision Section -->
      <h4 class="font-semibold text-secondary-700 text-sm border-b border-secondary-200 pb-2 flex-shrink-0">{{ $t('sales_dashboard.commission') }}</h4>
      
      <template v-if="calculatorProducts.length > 0">
        <div class="mt-3 space-y-1.5 text-sm" :class="{ 'max-h-72 overflow-y-auto': contractPeriod >= 108 }">
          <div 
            v-for="(commission, index) in commissionByYear" 
            :key="index"
            class="flex justify-between items-center p-1.5 bg-amber-50 rounded"
          >
            <span class="text-amber-700 font-medium">{{ $t('sales_dashboard.year_label') }} {{ index + 1 }}</span>
            <span class="font-bold text-amber-800">{{ formatCurrency(commission) }}</span>
          </div>
          <div class="pt-3 mt-2 border-t-2 border-gray-800">
            <div class="flex justify-between items-center p-2 bg-purple-100 rounded-lg border border-purple-300">
              <span class="text-sm font-bold text-purple-700">{{ $t('sales_dashboard.total_label') }}:</span>
              <span class="text-lg font-bold text-purple-800">{{ formatCurrency(commissionTotal) }}</span>
            </div>
          </div>
        </div>
      </template>
      <div v-else class="text-center py-4 text-secondary-400">
        <p class="text-xs">{{ $t('sales_dashboard.add_products_to_calculate') }}</p>
      </div>

      <!-- Spacer -->
      <div class="flex-1 min-h-2"></div>

      <!-- Umeå Finans Section -->
      <h4 class="font-semibold text-secondary-700 text-sm border-b border-secondary-200 pb-2 mt-2 flex-shrink-0">Umeå Finans</h4>
      
      <template v-if="calculatorProducts.length > 0">
        <div class="mt-3 space-y-2.5 text-sm">
          <div class="flex justify-between items-center">
            <span class="text-secondary-600">{{ $t('sales_dashboard.object_value') }}: </span>
            <button 
              @click="copyToClipboard(totalObjectValue)"
              class="font-bold text-secondary-800 hover:text-securdia-blue hover:underline cursor-pointer transition-colors"
              :title="$t('sales_dashboard.click_to_copy')"
            >
              {{ formatNumber(totalObjectValue) }} kr
            </button>
          </div>
          <div>
            <label class="block text-secondary-600 mb-0.5">{{ $t('sales_dashboard.monthly_rent_prelease') }}:</label>
            <input 
              type="number" 
              v-model.number="preleaseMonthlyRent"
              min="0"
              class="w-full px-2 py-1.5 border border-secondary-300 rounded text-sm focus:ring-1 focus:ring-securdia-blue"
            >
          </div>
          <div class="flex justify-between items-center">
            <span class="text-secondary-600">{{ $t('sales_dashboard.service_per_month') }}:</span>
            <button 
              @click="copyToClipboard(preleaseServicePerMonth)"
              class="font-bold text-secondary-800 hover:text-securdia-blue hover:underline cursor-pointer transition-colors"
              :title="$t('sales_dashboard.click_to_copy')"
            >
              {{ formatNumber(preleaseServicePerMonth) }} kr
            </button>
          </div>
          <div class="p-2 rounded space-y-1" :class="preleaseKontroll === 0 ? 'bg-green-100' : 'bg-red-100'">
            <div class="text-xs" :class="preleaseKontroll === 0 ? 'text-green-700' : 'text-red-700'">
              {{ $t('sales_dashboard.verify_prelease') }}
            </div>
            <div class="flex justify-between items-center text-sm" :class="preleaseKontroll === 0 ? 'text-green-700' : 'text-red-700'">
              <span>{{ formatCurrency(preleaseServicePerMonth) }} + {{ formatCurrency(preleaseMonthlyRent) }}</span>
              <span class="font-bold">= {{ formatCurrency(monthlyRevenue) }}</span>
            </div>
          </div>
        </div>
      </template>
      <div v-else class="text-center py-2 text-secondary-400">
        <p class="text-[10px]">{{ $t('sales_dashboard.add_products_to_calculate') }}</p>
      </div>
    </div>
      </div>
    </div>

    <!-- Delete Offer Confirmation Modal -->
    <div v-if="showDeleteOfferModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" @click.self="cancelDeleteOffer">
      <div class="fixed inset-0 bg-black bg-opacity-40"></div>
      <div class="relative bg-white rounded-lg shadow-xl p-5 w-full max-w-md z-10">
        <h3 class="text-sm font-bold text-secondary-800 mb-4 border-b border-secondary-200 pb-2">{{ $t('sales_dashboard.delete_offer') }}</h3>
        
        <div class="space-y-4">
          <div class="p-3 bg-red-50 rounded-lg border border-red-200">
            <p class="text-sm text-secondary-700">
              {{ $t('sales_dashboard.delete_offer_confirm', { name: offerToDelete ? offerToDelete.name : '' }) }}
            </p>
          </div>
        </div>
        
        <div class="flex justify-end gap-2 mt-5 pt-3 border-t border-secondary-200">
          <button 
            @click="cancelDeleteOffer"
            class="px-4 py-1.5 text-xs border border-secondary-300 rounded-md hover:bg-secondary-50"
          >
            {{ $t('sales_dashboard.cancel') }}
          </button>
          <button 
            @click="confirmDeleteOffer"
            class="px-4 py-1.5 text-xs text-white bg-red-500 rounded-md hover:bg-red-600"
          >
            {{ $t('sales_dashboard.delete_offer') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Save Offer Modal -->
    <div v-if="showOfferNameModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" @click.self="cancelSaveOffer">
      <div class="fixed inset-0 bg-black bg-opacity-40"></div>
      <div class="relative bg-white rounded-lg shadow-xl p-5 w-full max-w-md z-10">
        <h3 class="text-sm font-bold text-secondary-800 mb-4 border-b border-secondary-200 pb-2">{{ isEditingOffer ? $t('sales_dashboard.update_offer_title') : $t('sales_dashboard.name_offer_title') }}</h3>
        
        <div class="space-y-4">
          <div>
            <label class="block text-xs font-medium text-secondary-600 mb-1">{{ $t('sales_dashboard.offer_name_label') }}</label>
            <input 
              v-model="offerName"
              type="text"
              class="w-full px-3 py-2 border border-secondary-300 rounded-md text-sm focus:ring-1 focus:ring-securdia-blue focus:border-securdia-blue"
              :placeholder="$t('sales_dashboard.offer_name_placeholder')"
              @keyup.enter="confirmSaveOffer"
              ref="offerNameInput"
            />
          </div>

          <div>
            <label class="block text-xs font-medium text-secondary-600 mb-1">{{ $t('sales_dashboard.customer_name') }}</label>
            <input 
              v-model="offerCustomerName"
              type="text"
              class="w-full px-3 py-2 border border-secondary-300 rounded-md text-sm focus:ring-1 focus:ring-securdia-blue focus:border-securdia-blue"
              :placeholder="$t('sales_dashboard.customer_name_placeholder')"
              @keyup.enter="confirmSaveOffer"
            />
          </div>

          <div>
            <label class="block text-xs font-medium text-secondary-600 mb-1">{{ $t('sales_dashboard.department_name') }}</label>
            <input 
              v-model="offerDepartmentName"
              type="text"
              class="w-full px-3 py-2 border border-secondary-300 rounded-md text-sm focus:ring-1 focus:ring-securdia-blue focus:border-securdia-blue"
              :placeholder="$t('sales_dashboard.department_name_placeholder')"
              @keyup.enter="confirmSaveOffer"
            />
          </div>

          <!-- Summary preview -->
          <div v-if="calculatorProducts.length > 0" class="p-2 bg-blue-50 rounded border border-blue-200">
            <p class="text-[10px] font-medium text-blue-700 mb-1">{{ $t('sales_dashboard.offer_summary') }}:</p>
            <div class="text-[10px] text-blue-800 space-y-0.5">
              <div class="flex justify-between">
                <span>{{ $t('sales_dashboard.products') }}:</span>
                <span class="font-semibold">{{ unitCount }} {{ $t('sales_dashboard.units_suffix') }}</span>
              </div>
              <div class="flex justify-between">
                <span>{{ $t('sales_dashboard.contract_period_label') }}:</span>
                <span class="font-semibold">{{ contractPeriod }} {{ $t('sales_dashboard.months') }}</span>
              </div>
              <div class="flex justify-between">
                <span>TB:</span>
                <span class="font-semibold">{{ totalMarginPercent.toFixed(1) }}% ({{ formatCurrency(totalProfit) }})</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="flex justify-end gap-2 mt-5 pt-3 border-t border-secondary-200">
          <button 
            @click="cancelSaveOffer"
            class="px-4 py-1.5 text-xs border border-secondary-300 rounded-md hover:bg-secondary-50"
          >
            {{ $t('sales_dashboard.cancel') }}
          </button>
          <button 
            @click="confirmSaveOffer"
            :disabled="!offerName.trim() || !offerCustomerName.trim()"
            class="px-4 py-1.5 text-xs text-white rounded-md hover:opacity-90 bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isEditingOffer ? $t('sales_dashboard.update') : $t('sales_dashboard.save') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Share Offer Modal -->
    <div v-if="showShareModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" @click.self="cancelShare">
      <div class="fixed inset-0 bg-black bg-opacity-40"></div>
      <div class="relative bg-white rounded-lg shadow-xl p-5 w-full max-w-md z-10">
        <h3 class="text-sm font-bold text-secondary-800 mb-4 border-b border-secondary-200 pb-2">{{ $t('sales_dashboard.share_offer_title') }}</h3>
        
        <div class="space-y-3">
          <p class="text-xs text-secondary-600">{{ $t('sales_dashboard.share_offer_description') }}</p>
          
          <div v-if="availableSellers.length > 0" class="max-h-60 overflow-y-auto space-y-1 border border-secondary-200 rounded-lg p-2">
            <label 
              v-for="seller in availableSellers" 
              :key="seller._id" 
              class="flex items-center gap-2 p-1.5 rounded hover:bg-secondary-50 cursor-pointer"
            >
              <input 
                type="checkbox" 
                :value="seller.email" 
                v-model="selectedShareSellers"
                class="rounded border-secondary-300 text-blue-500 focus:ring-blue-500"
              />
              <span class="text-xs text-secondary-700">{{ seller.name || seller.email }}</span>
            </label>
          </div>
          <div v-else class="text-center py-4 text-secondary-400">
            <p class="text-xs">{{ $t('sales_dashboard.no_sellers_available') }}</p>
          </div>
        </div>
        
        <div class="flex justify-end gap-2 mt-5 pt-3 border-t border-secondary-200">
          <button 
            @click="cancelShare"
            class="px-4 py-1.5 text-xs border border-secondary-300 rounded-md hover:bg-secondary-50"
          >
            {{ $t('sales_dashboard.cancel') }}
          </button>
          <button 
            @click="confirmShare"
            class="px-4 py-1.5 text-xs text-white bg-blue-500 rounded-md hover:bg-blue-600"
          >
            {{ $t('sales_dashboard.share') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  SIMCARD_CATEGORIES,
  calculateCommissionByYear,
  formatCurrency as sharedFormatCurrency,
  formatNumber as sharedFormatNumber
} from '@/utils/commission';

export default {
  name: 'GrossMarginCalculator',
  props: {
    products: {
      type: Array,
      default: () => []
    },
    subcategories: {
      type: Array,
      default: () => []
    },
    user: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selectedProductId: '',
      selectedCategory: '',
      selectedSubcategory: '',
      contractPeriod: 60,
      selectedAlarmCenter: 'tempest',
      selectedSimcard: 'tele2',
      calculatorProducts: [],
      preleaseMonthlyRent: 0,
      otherCost: 0,
      activeSeller: null,
      activeSellerID: null,
      resolvedSellerID: null, // seller._id resolved from admin email lookup
      activeSellerAgreements: [],
      selectedAgreement: null,
      // Offers
      sellerOffers: [],
      selectedOffer: null,
      showOfferNameModal: false,
      offerName: '',
      offerCustomerName: '',
      offerDepartmentName: '',
      showDeleteOfferModal: false,
      offerToDelete: null,
      exportingPdf: false,
      // Share
      showShareModal: false,
      availableSellers: [],
      selectedShareSellers: []
    };
  },
  computed: {
    // Check if an agreement is loaded into the calculator (products should be read-only)
    isAgreementLoaded() {
      return !!(this.selectedAgreement && this.calculatorProducts.length > 0);
    },
    
    // Check if an offer is loaded into the calculator (products should be read-only)
    isOfferLoaded() {
      return !!(this.selectedOffer && this.calculatorProducts.length > 0);
    },

    // Unified check: only agreement locks the panels (offers are editable)
    isItemLoaded() {
      return this.isAgreementLoaded;
    },

    // Check if the current user is the owner of the selected offer.
    isOfferOwner() {
      if (!this.selectedOffer || !this.activeSellerID) return false;
      const offer = this.sellerOffers.find(o => o._id === this.selectedOffer);
      if (!offer) return false;
      if (offer.seller_id === this.activeSellerID) return true;
      // Admin whose email matched a seller record: check that seller's _id
      if (this.resolvedSellerID && offer.seller_id === this.resolvedSellerID) return true;
      return false;
    },

    // Build alarm center options from product catalog (category: alarm_central_supplier)
    alarmCenterOptions() {
      return this.products
        .filter(p => p.category === 'alarm_central_supplier')
        .map(p => ({
          value: (p.name || '').trim().toLowerCase(),
          label: p.name || '',
          cost: p.purchase_price || 0
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'sv'));
    },

    // Build simcard options from product catalog (category: simcard_supplier)
    simcardOptions() {
      return this.products
        .filter(p => p.category === 'simcard_supplier')
        .map(p => ({
          value: (p.name || '').trim().toLowerCase(),
          label: p.name || '',
          cost: p.purchase_price || 0
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'sv'));
    },

    // Check if we're editing an existing offer
    isEditingOffer() {
      return !!(this.selectedOffer && this.sellerOffers.find(o => o._id === this.selectedOffer));
    },
    
    // Get unique categories from products with labels
    categoryOptions() {
      const catKeys = {
        'alarm': 'cat_personal_alarm',
        'tag': 'cat_tags',
        'display': 'cat_display',
        'ajax': 'cat_perimeter',
        'vehicle_tracker': 'cat_vehicle_tracker',
        'software': 'cat_software',
        'simcard': 'cat_simcard',
        'accessory': 'cat_accessory',
        'simcard_supplier': 'cat_simcard_supplier',
        'alarm_central_supplier': 'cat_alarm_central_supplier',
      };
      
      const excludedCategories = new Set(['kategori', 'other']);

      const cats = new Set();
      this.products.forEach(p => {
        if (p.category && !excludedCategories.has(p.category)) cats.add(p.category);
      });
      return Array.from(cats)
        .map(cat => ({
          value: cat,
          label: catKeys[cat] ? this.$t(`sales_dashboard.${catKeys[cat]}`) : cat
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'sv'));
    },

    // Subcategory options based on selected category
    subcategoryOptions() {
      const options = [{ value: '', label: this.$t('sales_dashboard.filter_all_subcategories') }];
      if (this.selectedCategory && this.subcategories.length > 0) {
        const subs = this.subcategories
          .filter(s => s.parentCategory === this.selectedCategory)
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        subs.forEach(s => options.push({ value: s._id, label: s.displayName }));
      }
      return options;
    },

    // Filter products by category and subcategory
    filteredProducts() {
      let filtered = this.products;

      // Filter by category first
      if (this.selectedCategory) {
        filtered = filtered.filter(p => p.category === this.selectedCategory);
      }

      // Filter by subcategory if selected
      if (this.selectedSubcategory) {
        filtered = filtered.filter(p => {
          if (!p.subcategory) return false;
          // Compare as strings since subcategory can be ObjectId or string
          const productSubcatId = typeof p.subcategory === 'object' ? p.subcategory._id : p.subcategory;
          return String(productSubcatId) === String(this.selectedSubcategory);
        });
      }

      // Filter by having appropriate price (check product or variants)
      filtered = filtered.filter(p => {
        // Check if product itself has prices
        const hasOwnPrices = p.recommended_rental_price > 0 || p.recommended_cash_price > 0 || p.purchase_price > 0;
        // Check if any variant has prices
        const hasVariantPrices = p.variants && p.variants.some(v => 
          v.recommended_rental_price > 0 || v.recommended_cash_price > 0 || v.purchase_price > 0
        );
        return hasOwnPrices || hasVariantPrices;
      });

      return filtered.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sv'));
    },

    // Flatten products with variants into selectable items
    selectableProducts() {
      const items = [];
      
      for (const product of this.filteredProducts) {
        // Check if product has active variants
        const activeVariants = product.variants?.filter(v => v.isActive !== false) || [];
        
        if (activeVariants.length > 0) {
          // Add each variant as a selectable item
          for (const variant of activeVariants) {
            items.push({
              id: `${product._id}__${variant.articleNumber || variant.name}`,
              productId: product._id,
              name: `${product.name} - ${variant.name}`,
              category: product.category,
              variant: variant,
              // Use variant prices, fallback to product prices
              purchase_price: variant.purchase_price ?? product.purchase_price ?? 0,
              recommended_rental_price: variant.recommended_rental_price ?? product.recommended_rental_price ?? 0,
              recommended_cash_price: variant.recommended_cash_price ?? product.recommended_cash_price ?? 0
            });
          }
        } else {
          // No variants, add the product itself
          items.push({
            id: product._id,
            productId: product._id,
            name: product.name,
            category: product.category,
            variant: null,
            purchase_price: product.purchase_price ?? 0,
            recommended_rental_price: product.recommended_rental_price ?? 0,
            recommended_cash_price: product.recommended_cash_price ?? 0
          });
        }
      }
      
      return items.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sv'));
    },
    
    enrichedProducts() {
      return this.calculatorProducts.map(item => {
        // Cash purchase: one-time sale, no services
        if (item.isCashPurchase) {
          const lineTotal = item.quantity * item.salePrice;
          const lineCost = item.quantity * (item.purchasePrice || 0);
          const lineProfit = lineTotal - lineCost;
          const marginPercent = lineTotal > 0 ? (lineProfit / lineTotal) * 100 : 0;
          
          return {
            ...item,
            serviceCostPerUnit: 0,
            totalCostPerMonth: 0,
            lineTotal,
            lineCost,
            lineProfit,
            marginPercent,
            isCash: true
          };
        }
        
        // Rental: Calculate service costs for this product
        const hasAlarmCenter = item.category === 'alarm';
        const hasSimcard = SIMCARD_CATEGORIES.includes(item.category);
        
        const alarmCenterCostPerUnit = hasAlarmCenter ? this.selectedAlarmCenterCost : 0;
        const simcardCostPerUnit = hasSimcard ? this.selectedSimcardCost : 0;
        const serviceCostPerUnit = alarmCenterCostPerUnit + simcardCostPerUnit;
        
        // Total cost per unit per month (product cost + service costs)
        const totalCostPerMonth = item.cost + serviceCostPerUnit;
        
        // Total over contract period
        const lineTotal = item.quantity * item.salePrice * this.contractPeriod;
        const lineCost = item.quantity * totalCostPerMonth * this.contractPeriod;
        const lineProfit = lineTotal - lineCost;
        const marginPercent = lineTotal > 0 ? (lineProfit / lineTotal) * 100 : 0;
        
        return {
          ...item,
          serviceCostPerUnit,
          totalCostPerMonth,
          lineTotal,
          lineCost,
          lineProfit,
          marginPercent,
          isCash: false
        };
      });
    },
    
    totalRevenue() {
      return this.enrichedProducts.reduce((sum, item) => sum + item.lineTotal, 0);
    },
    
    totalCost() {
      // Total cost = Fixed costs (one-time) + Variable costs (recurring over contract period)
      return this.fixedCosts.total + this.variableCosts.total;
    },
    
    totalProfit() {
      return this.totalRevenue - this.totalCost;
    },
    
    totalMarginPercent() {
      return this.totalRevenue > 0 ? (this.totalProfit / this.totalRevenue) * 100 : 0;
    },

    // Counts
    productCount() {
      return this.calculatorProducts.length;
    },

    unitCount() {
      return this.calculatorProducts.reduce((sum, item) => sum + item.quantity, 0);
    },

    // Alarm center quantity = products in 'alarm' (personlarm) category (only rental, not cash)
    alarmCenterQuantity() {
      return this.calculatorProducts
        .filter(p => p.category === 'alarm' && !p.isCashPurchase)
        .reduce((sum, item) => sum + item.quantity, 0);
    },

    // Simcard quantity = products in 'alarm', 'display', 'vehicle_tracker' categories (only rental, not cash)
    simcardQuantity() {
      return this.calculatorProducts
        .filter(p => SIMCARD_CATEGORIES.includes(p.category) && !p.isCashPurchase)
        .reduce((sum, item) => sum + item.quantity, 0);
    },

    // Count of personlarm (alarm category) for SOS app cost (only rental, not cash)
    personlarmCount() {
      return this.calculatorProducts
        .filter(p => p.category === 'alarm' && !p.isCashPurchase)
        .reduce((sum, item) => sum + item.quantity, 0);
    },

    // SOS International App cost (995 kr per personlarm)
    sosAppTotalCost() {
      return this.personlarmCount * 995;
    },

    // Get selected alarm center cost
    selectedAlarmCenterCost() {
      const ac = this.alarmCenterOptions.find(a => a.value === this.selectedAlarmCenter);
      return ac ? ac.cost : 0;
    },

    // Get selected simcard cost
    selectedSimcardCost() {
      const sim = this.simcardOptions.find(s => s.value === this.selectedSimcard);
      return sim ? sim.cost : 0;
    },

    // Total alarm center cost per month
    alarmCenterTotalCost() {
      return this.alarmCenterQuantity * this.selectedAlarmCenterCost;
    },

    // Total simcard cost per month
    simcardTotalCost() {
      return this.simcardQuantity * this.selectedSimcardCost;
    },

    // Total services cost per month
    servicesTotalCost() {
      return this.alarmCenterTotalCost + this.simcardTotalCost;
    },

    // Fixed costs (one-time costs)
    fixedCosts() {
      // Objektsvärde (purchase prices for rental) + Kontantköp inköp + Startavgifter (SOS App) + Extra avgifter
      const otherCostValue = Number(this.otherCost) || 0;
      return {
        objectValue: this.totalObjectValue,
        cashPurchaseCost: this.totalCashPurchaseCost,
        startupFees: this.sosAppTotalCost,
        other: otherCostValue,
        total: this.totalObjectValue + this.totalCashPurchaseCost + this.sosAppTotalCost + otherCostValue
      };
    },

    // Variable costs (recurring monthly costs over contract period)
    variableCosts() {
      const alarmCenterTotal = this.alarmCenterTotalCost * this.contractPeriod;
      const simcardTotal = this.simcardTotalCost * this.contractPeriod;
      // Simcard products added to the calculator are also monthly costs
      const simcardProductsTotal = this.calculatorProducts
        .filter(p => p.category === 'simcard' && !p.isCashPurchase)
        .reduce((sum, item) => sum + (item.quantity * (item.purchasePrice || 0) * this.contractPeriod), 0);
      return {
        alarmCenter: alarmCenterTotal,
        simcard: simcardTotal,
        simcardProducts: simcardProductsTotal,
        total: alarmCenterTotal + simcardTotal + simcardProductsTotal
      };
    },

    // Monthly calculations (only rental products)
    monthlyRevenue() {
      return this.calculatorProducts
        .filter(item => !item.isCashPurchase)
        .reduce((sum, item) => sum + (item.quantity * item.salePrice), 0);
    },

    monthlyCost() {
      // enrichedProducts.totalCostPerMonth already includes service costs (only for rental)
      return this.enrichedProducts
        .filter(item => !item.isCash)
        .reduce((sum, item) => sum + (item.quantity * item.totalCostPerMonth), 0);
    },

    monthlyMargin() {
      return this.monthlyRevenue - this.monthlyCost;
    },

    // Commission calculations (7% of total TB)
    // Dynamic based on contract period in years
    // Year 1: 50% of 7% of TB (or 100% if 1 year contract)
    // Year 2+: 50% of previous year, last two years are the same
    // For mixed cash + services: cash commission is year 1, remaining years are software
    
    contractYears() {
      return this.contractPeriod / 12;
    },
    
    // TB from cash products (hardware sold as cash)
    cashProductTB() {
      return this.calculatorProducts
        .filter(item => item.isCashPurchase)
        .reduce((sum, item) => {
          const revenue = item.quantity * item.salePrice;
          const cost = item.quantity * (item.purchasePrice || 0);
          return sum + (revenue - cost);
        }, 0);
    },
    
    // TB from software/services (rental products over contract period)
    softwareProductTB() {
      return this.totalProfit - this.cashProductTB;
    },
    
    // Commission for cash products (all in year 1)
    cashCommission() {
      return this.cashProductTB * 0.07;
    },
    
    // Total commission for software over all years
    totalSoftwareCommission() {
      return this.softwareProductTB * 0.07;
    },
    
    // No cost deductions - commission based purely on calculated profit
    commissionCostDeduction() {
      return 0;
    },

    _commissionResult() {
      const adjustedProfit = this.totalProfit - this.commissionCostDeduction;
      return calculateCommissionByYear(adjustedProfit, this.contractPeriod);
    },

    commissionByYear() {
      return this._commissionResult.byYear;
    },

    commissionTotal() {
      return this._commissionResult.total;
    },

    // Umeå Finans calculations
    totalObjectValue() {
      // Sum of RENTAL hardware products: quantity * purchase_price (inköpskostnad)
      // Cash products and simcard products (monthly cost) are excluded
      return this.calculatorProducts
        .filter(item => !item.isCashPurchase && item.category !== 'simcard')
        .reduce((sum, item) => {
          return sum + (item.quantity * (item.purchasePrice || 0));
        }, 0);
    },
    
    // Total cash purchase cost (purchase price for cash items)
    totalCashPurchaseCost() {
      return this.calculatorProducts
        .filter(item => item.isCashPurchase)
        .reduce((sum, item) => {
          return sum + (item.quantity * (item.purchasePrice || 0));
        }, 0);
    },
    
    // Total cash purchase revenue
    totalCashPurchaseRevenue() {
      return this.calculatorProducts
        .filter(item => item.isCashPurchase)
        .reduce((sum, item) => {
          return sum + (item.quantity * item.salePrice);
        }, 0);
    },

    preleaseServicePerMonth() {
      // Monthly revenue for customer - the prelease monthly rent input
      return this.monthlyRevenue - this.preleaseMonthlyRent;
    },

    preleaseKontroll() {
      // Difference: (Månadshyra + Service) should equal monthlyRevenue
      // If this is 0, the values are correct
      return (this.preleaseMonthlyRent + this.preleaseServicePerMonth) - this.monthlyRevenue;
    },

    // Break-even analysis (only for rental products)
    breakEvenMonths() {
      const rentalProducts = this.calculatorProducts.filter(p => !p.isCashPurchase);
      if (rentalProducts.length === 0) return 0;

      // Initial investment = purchase price for rental units only
      const initialInvestment = rentalProducts.reduce((sum, item) => {
        return sum + (item.quantity * (item.purchasePrice || 0));
      }, 0);

      if (this.monthlyMargin <= 0) return this.contractPeriod; // Never breaks even

      // Months to recover investment
      return Math.ceil(initialInvestment / this.monthlyMargin);
    },
    
    // Check if there are any cash purchases
    hasCashPurchases() {
      return this.calculatorProducts.some(p => p.isCashPurchase);
    },
    
    // Check if there are any rental products
    hasRentalProducts() {
      return this.calculatorProducts.some(p => !p.isCashPurchase);
    },
    
    // Check if this is a pure cash purchase (no software/services needed)
    // True if: all hardware products are cash AND no rental products requiring services
    isPureCashPurchase() {
      if (this.calculatorProducts.length === 0) return false;
      // All products must be cash purchases
      const allCash = this.calculatorProducts.every(p => p.isCashPurchase);
      // No simcard or alarm center services needed (these are already 0 if all cash)
      const noServices = this.simcardQuantity === 0 && this.alarmCenterQuantity === 0;
      return allCash && noServices;
    }
  },
  watch: {
    calculatorProducts: {
      handler() {
        this.$forceUpdate();
      },
      deep: true
    },
    // Set default alarm center/simcard when options become available from products prop
    alarmCenterOptions: {
      handler(options) {
        // If current selection doesn't exist in the new options, fall back to first available
        if (options.length && !options.find(o => o.value === this.selectedAlarmCenter)) {
          this.selectedAlarmCenter = options[0].value;
        }
      },
      immediate: true
    },
    simcardOptions: {
      handler(options) {
        if (options.length && !options.find(o => o.value === this.selectedSimcard)) {
          this.selectedSimcard = options[0].value;
        }
      },
      immediate: true
    },
    // Reset product selection when category changes
    selectedCategory() {
      this.selectedProductId = '';
    },
    // Auto-set contract period to 12 months for pure cash purchases
    isPureCashPurchase: {
      handler(isPure) {
        if (isPure && this.contractPeriod !== 12) {
          this.contractPeriod = 12;
        }
      },
      immediate: true
    },
    // Watch for user prop changes
    user: {
      handler(newUser) {
        if (newUser) {
          this.getActiveSeller();
        }
      },
      immediate: true
    },
    // Watch for agreement deselection (default option selected)
    selectedAgreement: {
      handler(newValue, oldValue) {
        // If user selects the default/null option after having an agreement selected, reset
        if (newValue === null && oldValue !== null && oldValue !== undefined) {
          this.reset();
        }
      }
    },
    // Watch for offer deselection (default option selected)
    selectedOffer: {
      handler(newValue, oldValue) {
        // If user selects the default/null option after having an offer selected, reset
        if (newValue === null && oldValue !== null && oldValue !== undefined) {
          this.reset();
        }
      }
    }
  },
  mounted() {
    // User data is now handled by the watcher
  },
  methods: {
    getActiveSeller(){
      if (!this.user) {
        //no user
        return;
      }
      
      // If user is a seller with seller_id, use existing logic (faster)
      if (this.user.role === 'seller' && this.user.seller_id) {
        this.activeSeller = this.user.name || this.user.username || 'Unknown';
        this.activeSellerID = this.user.seller_id;
        this.getActiveSellerAgreements(this.user.seller_id);
        this.fetchSellerOffers(this.user.seller_id);
      } 
      // If user is admin: set seller info immediately (for offers), then try email lookup (for agreements)
      else if (this.user.role === 'admin') {
        // Set immediately so offers can always be saved
        this.activeSeller = this.user.username || 'Admin';
        this.activeSellerID = this.user.username || 'admin';
        this.fetchSellerOffers(this.activeSellerID);
        
        // Try to find a matching seller by email for agreements
        if (this.user.email) {
          this.lookupSellerAgreementsByEmail(this.user.email);
        }
      }
    },
    
    lookupSellerAgreementsByEmail(userEmail) {
      this.axios.get(`${process.env.VUE_APP_SERVER_URL}/sellers`)
        .then(response => {
          const sellers = response.data.data || response.data || [];
          const matchingSeller = sellers.find(seller => {
            return seller.email && seller.email.toLowerCase() === userEmail.toLowerCase();
          });
          
          if (matchingSeller) {
            this.resolvedSellerID = matchingSeller._id;
            this.getActiveSellerAgreements(matchingSeller._id);
          }
        })
        .catch(error => {
          console.error('Error looking up seller for agreements:', error);
        });
    },
    getActiveSellerAgreements(seller_id){
      
      this.axios.get(`${process.env.VUE_APP_SERVER_URL}/agreement/seller/${seller_id}`)
        .then(response => {
          this.activeSellerAgreements = response.data.agreements || [];
        })
        .catch(error => {
          console.error('Error fetching active agreements:', error);
          this.activeSellerAgreements = [];
        });
    },
    
    // ---- Offer Methods ----
    fetchSellerOffers(seller_id) {
      this.axios.get(`${process.env.VUE_APP_SERVER_URL}/offers/seller/${seller_id}`)
        .then(response => {
          this.sellerOffers = response.data.offers || [];
        })
        .catch(error => {
          console.error('Error fetching seller offers:', error);
          this.sellerOffers = [];
        });
    },

    loadOfferToCalculator() {
      if (!this.selectedOffer) return;

      const offer = this.sellerOffers.find(o => o._id === this.selectedOffer);
      if (!offer) {
        console.error('Offer not found in list');
        return;
      }

      // Set calculator settings from offer
      if (offer.contract_period) this.contractPeriod = offer.contract_period;
      if (offer.alarm_center) {
        const acMatch = this.alarmCenterOptions.find(a => a.value === offer.alarm_center);
        if (acMatch) this.selectedAlarmCenter = offer.alarm_center;
      }
      if (offer.sim_card) {
        const simMatch = this.simcardOptions.find(s => s.value === offer.sim_card);
        if (simMatch) this.selectedSimcard = offer.sim_card;
      }
      if (offer.other_cost) this.otherCost = offer.other_cost;

      // Clear existing products and load offer products
      this.calculatorProducts = [];

      if (Array.isArray(offer.products) && offer.products.length > 0) {
        offer.products.forEach(offerProduct => {
          try {
            // Find matching product in catalog
            let productMatch = this.products.find(p =>
              p._id === offerProduct.product_id || String(p._id) === String(offerProduct.product_id)
            );

            const purchasePrice = productMatch ? (productMatch.purchase_price || 0) : (offerProduct.purchase_price || 0);
            const isCash = offerProduct.is_cash_purchase || false;
            const offerCategory = offerProduct.category || (productMatch ? productMatch.category : '');
            const cost = isCash ? 0 : (offerCategory === 'simcard' ? purchasePrice : Math.round(purchasePrice / this.contractPeriod));

            this.calculatorProducts.push({
              itemId: offerProduct.variant_id || offerProduct.product_id,
              productId: offerProduct.product_id,
              name: offerProduct.name,
              category: offerProduct.category || (productMatch ? productMatch.category : ''),
              variantName: offerProduct.variant_name || null,
              quantity: offerProduct.quantity || 1,
              salePrice: offerProduct.sale_price || 0,
              cost: cost,
              purchasePrice: purchasePrice,
              cashPrice: offerProduct.cash_price || purchasePrice,
              originalRentalPrice: offerProduct.sale_price || 0,
              originalCost: cost,
              isCashPurchase: isCash
            });
          } catch (err) {
            console.error('Error loading offer product:', err);
          }
        });
      }
    },

    openSaveOfferModal() {
      if (this.calculatorProducts.length === 0) return;

      // If an offer is selected, pre-fill with its data for editing
      if (this.isEditingOffer) {
        const offer = this.sellerOffers.find(o => o._id === this.selectedOffer);
        this.offerName = offer.name || '';
        this.offerCustomerName = offer.customer_name || '';
        this.offerDepartmentName = offer.department_name || '';
      } else {
        this.offerName = '';
        this.offerCustomerName = '';
        this.offerDepartmentName = '';
      }

      this.showOfferNameModal = true;
      this.$nextTick(() => {
        if (this.$refs.offerNameInput) {
          this.$refs.offerNameInput.focus();
        }
      });
    },

    cancelSaveOffer() {
      this.showOfferNameModal = false;
      this.offerName = '';
      this.offerCustomerName = '';
      this.offerDepartmentName = '';
    },

    confirmSaveOffer() {
      const name = this.offerName.trim();
      const customerName = this.offerCustomerName.trim();
      if (!name || !customerName) return;
      if (!this.activeSellerID) {
        console.error('No active seller ID, cannot save offer');
        return;
      }

      // Build offer data from current calculator state
      const offerData = {
        name,
        customer_name: customerName,
        department_name: this.offerDepartmentName.trim(),
        seller_id: this.activeSellerID,
        seller_name: this.activeSeller,
        contract_period: this.contractPeriod,
        alarm_center: this.selectedAlarmCenter,
        sim_card: this.selectedSimcard,
        other_cost: Number(this.otherCost) || 0,
        products: this.calculatorProducts.map(item => ({
          product_id: item.productId,
          variant_id: item.variantName ? item.itemId : null,
          variant_name: item.variantName || null,
          name: item.name,
          category: item.category,
          quantity: item.quantity,
          sale_price: item.salePrice,
          purchase_price: item.purchasePrice || 0,
          cost: item.cost,
          cash_price: item.cashPrice || 0,
          is_cash_purchase: item.isCashPurchase || false
        })),
        total_revenue: Math.round(this.totalRevenue),
        total_cost: Math.round(this.totalCost),
        total_profit: Math.round(this.totalProfit),
        margin_percent: Math.round(this.totalMarginPercent * 10) / 10,
        monthly_revenue: Math.round(this.monthlyRevenue)
      };

      // Update existing offer or create new
      const isUpdate = this.isEditingOffer;
      const request = isUpdate
        ? this.axios.put(`${process.env.VUE_APP_SERVER_URL}/offers/${this.selectedOffer}`, offerData)
        : this.axios.post(`${process.env.VUE_APP_SERVER_URL}/offers`, offerData);

      request
        .then(response => {
          const savedOffer = response.data.offer;
          if (savedOffer) {
            if (isUpdate) {
              // Replace the updated offer in the list
              const index = this.sellerOffers.findIndex(o => o._id === savedOffer._id);
              if (index !== -1) {
                this.$set(this.sellerOffers, index, savedOffer);
              }
            } else {
              this.sellerOffers.unshift(savedOffer);
              this.selectedOffer = savedOffer._id;
            }
          }
          this.showOfferNameModal = false;
          this.offerName = '';
          this.offerCustomerName = '';
          this.offerDepartmentName = '';
        })
        .catch(error => {
          console.error('Error saving offer:', error);
          alert(this.$t('sales_dashboard.offer_save_error'));
        });
    },

    deleteSelectedOffer() {
      if (!this.selectedOffer) return;

      const offer = this.sellerOffers.find(o => o._id === this.selectedOffer);
      if (!offer) return;

      this.offerToDelete = offer;
      this.showDeleteOfferModal = true;
    },

    confirmDeleteOffer() {
      if (!this.offerToDelete) return;

      this.axios.delete(`${process.env.VUE_APP_SERVER_URL}/offers/${this.offerToDelete._id}`)
        .then(() => {
          this.sellerOffers = this.sellerOffers.filter(o => o._id !== this.offerToDelete._id);
          if (this.selectedOffer === this.offerToDelete._id) {
            this.selectedOffer = null;
            this.reset();
          }
          this.showDeleteOfferModal = false;
          this.offerToDelete = null;
        })
        .catch(error => {
          console.error('Error deleting offer:', error);
          alert(this.$t('sales_dashboard.delete_offer_error'));
          this.showDeleteOfferModal = false;
          this.offerToDelete = null;
        });
    },

    cancelDeleteOffer() {
      this.showDeleteOfferModal = false;
      this.offerToDelete = null;
    },

    // ---- Share Offer Methods ----
    openShareModal() {
      if (!this.selectedOffer) return;

      const offer = this.sellerOffers.find(o => o._id === this.selectedOffer);
      if (!offer) return;

      // Pre-select sellers whose email is already in the access_list
      this.selectedShareSellers = [...(offer.access_list || [])];

      // Fetch available sellers
      this.axios.get(`${process.env.VUE_APP_SERVER_URL}/sellers`)
        .then(response => {
          const sellers = response.data.data || response.data || [];
          // Exclude the current user from the list (by email)
          const currentEmail = (this.user.email || '').toLowerCase();
          this.availableSellers = sellers.filter(s => {
            return (s.email || '').toLowerCase() !== currentEmail;
          });
          this.showShareModal = true;
        })
        .catch(error => {
          console.error('Error fetching sellers:', error);
          this.availableSellers = [];
          this.showShareModal = true;
        });
    },

    cancelShare() {
      this.showShareModal = false;
      this.selectedShareSellers = [];
      this.availableSellers = [];
    },

    confirmShare() {
      if (!this.selectedOffer) return;

      this.axios.put(`${process.env.VUE_APP_SERVER_URL}/offers/${this.selectedOffer}/share`, {
        access_list: this.selectedShareSellers
      })
        .then(response => {
          // Update the local offer with the new access_list
          const updatedOffer = response.data.offer;
          if (updatedOffer) {
            const index = this.sellerOffers.findIndex(o => o._id === updatedOffer._id);
            if (index !== -1) {
              this.$set(this.sellerOffers, index, updatedOffer);
            }
          }
          this.showShareModal = false;
          this.selectedShareSellers = [];
          this.availableSellers = [];
        })
        .catch(error => {
          console.error('Error sharing offer:', error);
          alert(this.$t('sales_dashboard.share_offer_error'));
        });
    },
    
    loadAgreementToCalculator() {
      if (!this.selectedAgreement) {
        return;
      }
      
      // Find the full agreement object
      const agreement = this.activeSellerAgreements.find(a => a.agreement_number === this.selectedAgreement);
      if (!agreement) {
        console.error('Agreement not found in list');
        return;
      }
      
      // Set contract period if available (must be a valid number)
      if (agreement.contract_period && Number.isFinite(Number(agreement.contract_period))) {
        this.contractPeriod = Number(agreement.contract_period);
      }
      
      // Set alarm center if available - fetch and map by ObjectId
      if (agreement.alarm_center) {
        this.axios.get(`${process.env.VUE_APP_SERVER_URL}/alarm-central`)
          .then(response => {
            const alarmCenters = response.data.data || [];
            const matchedCenter = alarmCenters.find(ac => ac._id === agreement.alarm_center);
            
            if (matchedCenter) {
              const centerNameLower = (matchedCenter.name || '').trim().toLowerCase();
              if (this.alarmCenterOptions.find(ac => ac.value === centerNameLower)) {
                this.selectedAlarmCenter = centerNameLower;
              }
            }
          })
          .catch(error => {
            console.error('Error fetching alarm centers:', error);
          });
      }
      
      // Set simcard if available
      if (agreement.sim_card && typeof agreement.sim_card === 'string') {
        const simcardLower = agreement.sim_card.trim().toLowerCase();
        if (this.simcardOptions.find(s => s.value === simcardLower)) {
          this.selectedSimcard = simcardLower;
        }
      }

      
      // Clear existing products
      this.calculatorProducts = [];
      
      // Load agreement products
      if (Array.isArray(agreement.products) && agreement.products.length > 0) {
        agreement.products.forEach(agreementProduct => {
          try {
          // Skip malformed entries
          if (!agreementProduct || typeof agreementProduct !== 'object') {
            console.warn('Skipping invalid product entry in agreement');
            return;
          }
          
          // Find matching parent product in catalog
          // For both regular and flattened variant products, `type` always matches the parent product
          let productMatch = this.products.find(p => 
            p._id === agreementProduct.product_id || 
            p.name === agreementProduct.name ||
            p.type === agreementProduct.type
          );
          
          if (!productMatch) {
            console.warn('Product not found in catalog:', agreementProduct.name, '(type:', agreementProduct.type, ')');
            return;
          }
          
          // Check if this is a variant product
          let itemId = productMatch._id;
          let displayName = productMatch.name;
          let purchasePrice = productMatch.purchase_price || 0;
          let recommendedRentalPrice = productMatch.recommended_rental_price || 0;
          let recommendedCashPrice = productMatch.recommended_cash_price || 0;
          let variantName = null;
          let foundVariant = null;
          let agreementVariant = null;
          
          // Check for variant info in two formats:
          // A) Nested format: agreementProduct.variants = [{name, qty, ...}]
          // B) Flattened format: agreementProduct.variant_id / variant_name (from order-based products)
          const hasNestedVariants = agreementProduct.variants && agreementProduct.variants.length > 0;
          const hasFlatVariant = agreementProduct.variant_id || agreementProduct.variant_name;
          
          if (hasNestedVariants) {
            // Format A: Nested variants array
            agreementVariant = agreementProduct.variants[0];
            
            // Find matching variant in product catalog
            if (productMatch.variants) {
              foundVariant = productMatch.variants.find(v => 
                v.name === agreementVariant.name || 
                v.articleNumber === agreementVariant.articleNumber
              );
              if (foundVariant) {
                itemId = `${productMatch._id}__${foundVariant.articleNumber || foundVariant.name}`;
                displayName = `${productMatch.name} - ${foundVariant.name}`;
                purchasePrice = foundVariant.purchase_price ?? productMatch.purchase_price ?? 0;
                recommendedRentalPrice = foundVariant.recommended_rental_price ?? productMatch.recommended_rental_price ?? 0;
                recommendedCashPrice = foundVariant.recommended_cash_price ?? productMatch.recommended_cash_price ?? 0;
                variantName = foundVariant.name;
              }
            }
          } else if (hasFlatVariant) {
            // Format B: Flattened variant (variant_id, variant_name, articleNumber at top level)
            // Build a pseudo-variant object from the flat fields for price extraction
            agreementVariant = {
              _id: agreementProduct.variant_id,
              name: agreementProduct.variant_name,
              articleNumber: agreementProduct.articleNumber,
              rental_price: agreementProduct.rental_price || agreementProduct.price,
              cash_price: agreementProduct.cash_price,
              qty: agreementProduct.qty
            };
            
            // Find matching variant in product catalog
            if (productMatch.variants) {
              foundVariant = productMatch.variants.find(v => 
                String(v._id) === String(agreementProduct.variant_id) ||
                v.name === agreementProduct.variant_name ||
                (agreementProduct.articleNumber && v.articleNumber === agreementProduct.articleNumber)
              );
              if (foundVariant) {
                itemId = `${productMatch._id}__${foundVariant.articleNumber || foundVariant.name}`;
                displayName = `${productMatch.name} - ${foundVariant.name}`;
                purchasePrice = foundVariant.purchase_price ?? productMatch.purchase_price ?? 0;
                recommendedRentalPrice = foundVariant.recommended_rental_price ?? productMatch.recommended_rental_price ?? 0;
                recommendedCashPrice = foundVariant.recommended_cash_price ?? productMatch.recommended_cash_price ?? 0;
                variantName = foundVariant.name;
              }
            }
          }
          
          // Determine if this is a cash purchase or rental
          // 1. If product type is explicitly cash/one_time → cash
          // 2. If agreement is cash/cash_services and product is NOT monthly/software → cash
          const isCashAgreement = agreement.agreement_category === 'cash' || agreement.agreement_category === 'cash_services';
          const isMonthlyOrSoftware = ['addon_monthly', 'other_monthly', 'software'].includes(agreementProduct.type) || 
                                      (productMatch.category && productMatch.category.toLowerCase() === 'software');
          const isCash = agreementProduct.type === 'cash' || agreementProduct.type === 'one_time' || 
                        (isCashAgreement && !isMonthlyOrSoftware);
          
          // Calculate cost: simcard products use a monthly cost directly; hardware is amortized
          const productCategory = agreementProduct.category || (productMatch ? productMatch.category : '');
          const cost = isCash ? 0 : (productCategory === 'simcard' ? purchasePrice : Math.round(purchasePrice / this.contractPeriod));
          
          // Get actual price from agreement (prioritize variant prices over product price)
          let salePrice = 0;
          let quantity = 1;
          
          if (agreementVariant) {
            // For variants, use rental_price or cash_price from the agreement variant
            salePrice = agreementVariant.rental_price || agreementVariant.cash_price || agreementVariant.price || 0;
            quantity = agreementVariant.qty || agreementVariant.quantity || 1;
          } else {
            // For non-variant products, use the product price
            salePrice = agreementProduct.price || 0;
            quantity = agreementProduct.qty || agreementProduct.quantity || 1;
          }
          
          // Fallback to recommended prices if no agreement price
          if (!salePrice) {
            salePrice = isCash ? recommendedCashPrice : recommendedRentalPrice;
          }
          
          this.calculatorProducts.push({
            itemId: itemId,
            productId: productMatch._id,
            name: displayName,
            category: agreementProduct.category || productMatch.category || 'other',
            variantName: variantName,
            quantity: quantity,
            salePrice: salePrice,
            cost: cost,
            purchasePrice: purchasePrice,
            originalRentalPrice: recommendedRentalPrice || salePrice,
            cashPrice: recommendedCashPrice || purchasePrice,
            isCashPurchase: isCash
          });
          } catch (err) {
            console.warn('Error processing agreement product, skipping:', agreementProduct?.name || 'unknown', err);
          }
        });
      }
    },
    getRecommendedPrice(product) {
      // Use rental price if available, otherwise estimate from cash price
      if (product.recommended_rental_price > 0) {
        return product.recommended_rental_price;
      }
      // Estimate: cash price / contract period as fallback
      return Math.round((product.recommended_cash_price || product.purchase_price || 0) / this.contractPeriod);
    },

    // Get recommended price from a selectable item (with variant support)
    getRecommendedPriceFromItem(item) {
      if (item.recommended_rental_price > 0) {
        return item.recommended_rental_price;
      }
      return Math.round((item.recommended_cash_price || item.purchase_price || 0) / this.contractPeriod);
    },

    // Get monthly cost from a selectable item (with variant support)
    getMonthlyCostFromItem(item) {
      const purchasePrice = item.purchase_price || 0;
      // Simcard and supplier products have a monthly purchase cost (not a one-time hardware cost)
      const monthlyCostCategories = ['simcard', 'simcard_supplier', 'alarm_central_supplier'];
      if (monthlyCostCategories.includes(item.category)) return purchasePrice;
      return Math.round(purchasePrice / this.contractPeriod);
    },

    // Get purchase price for a calculator item (handles variants)
    getItemPurchasePrice(calculatorItem) {
      // Use stored purchasePrice to avoid issues when category filter changes
      return calculatorItem.purchasePrice || 0;
    },

    getProductPurchasePrice(productId) {
      const product = this.products.find(p => p._id === productId);
      return product?.purchase_price || 0;
    },

    getMonthlyCost(product) {
      // Monthly cost for rental = purchase price / contract period (amortized)
      const purchasePrice = product.purchase_price || 0;
      return Math.round(purchasePrice / this.contractPeriod);
    },

    addProduct() {
      if (!this.selectedProductId) return;
      
      // Find the selectable item (could be product or variant)
      const item = this.selectableProducts.find(p => p.id === this.selectedProductId);
      if (!item) return;

      // Check if this exact item already exists in the list (by unique id)
      const existingIndex = this.calculatorProducts.findIndex(p => p.itemId === item.id);
      
      if (existingIndex !== -1) {
        // Item exists, increment quantity
        this.calculatorProducts[existingIndex].quantity += 1;
      } else {
        // New item, add to list
        const salePrice = this.getRecommendedPriceFromItem(item);
        const cost = this.getMonthlyCostFromItem(item);
        const purchasePrice = item.purchase_price || 0;
        
        this.calculatorProducts.push({
          itemId: item.id,
          productId: item.productId,
          name: item.name,
          category: item.category,
          variantName: item.variant?.name || null,
          quantity: 1,
          salePrice: salePrice,
          cost: cost,
          purchasePrice: purchasePrice,  // Store purchase price to avoid lookup issues
          cashPrice: item.recommended_cash_price || purchasePrice,  // Store cash price
          originalRentalPrice: salePrice,  // Store original rental price for toggle back
          originalCost: cost,  // Store original cost for toggle back
          isCashPurchase: false  // Toggle for cash vs rental
        });
      }
      
      this.selectedProductId = '';
    },
    
    removeProduct(index) {
      this.calculatorProducts.splice(index, 1);
    },
    
    toggleCashPurchase(index) {
      const item = this.calculatorProducts[index];
      this.setCashPurchase(index, !item.isCashPurchase);
    },
    
    setCashPurchase(index, isCash) {
      const item = this.calculatorProducts[index];
      
      // Skip if already in desired state
      if (item.isCashPurchase === isCash) return;
      
      // Update ALL hardware products to the same state
      this.calculatorProducts.forEach((product, i) => {
        if (this.isHardwareCategory(product.category)) {
          this.setSingleProductCashPurchase(i, isCash);
        }
      });
    },
    
    // Internal method to set cash/rental for a single product
    setSingleProductCashPurchase(index, isCash) {
      const item = this.calculatorProducts[index];
      
      // Skip if already in desired state
      if (item.isCashPurchase === isCash) return;
      
      item.isCashPurchase = isCash;
      
      // Update sale price and cost based on purchase type
      if (isCash) {
        // Switch to cash: use cash price, cost is purchase price (one-time)
        item.salePrice = item.cashPrice || item.purchasePrice || 0;
        item.cost = 0; // No monthly cost for cash purchases
      } else {
        // Switch back to rental: restore original rental values
        item.salePrice = item.originalRentalPrice || Math.round((item.purchasePrice || 0) / this.contractPeriod);
        item.cost = item.originalCost || Math.round((item.purchasePrice || 0) / this.contractPeriod);
      }
      
      // Force reactivity
      this.$set(this.calculatorProducts, index, { ...item });
    },
    
    isHardwareCategory(category) {
      // Categories that can be sold as cash (hardware products)
      const hardwareCategories = ['alarm', 'tag', 'display', 'ajax', 'vehicle_tracker', 'accessory'];
      return hardwareCategories.includes(category);
    },
    
    reset() {
      this.calculatorProducts = [];
      this.selectedProductId = '';
      this.selectedCategory = '';
      this.selectedSubcategory = '';
      this.contractPeriod = 60;
      this.selectedAlarmCenter = 'tempest';
      this.selectedSimcard = 'tele2';
      this.otherCost = 0;
      this.selectedAgreement = null;
      this.selectedOffer = null;
    },
    
    formatCurrency(value) {
      return sharedFormatCurrency(value);
    },
    
    formatNumber(value) {
      return sharedFormatNumber(value);
    },
    
    async copyToClipboard(value) {
      try {
        const numericValue = Math.round(value);
        await navigator.clipboard.writeText(numericValue.toString());
        // Optional: show feedback
        this.$emit('copied', numericValue);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    },

    formatCompact(value) {
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M kr`;
      }
      if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}k kr`;
      }
      return `${Math.round(value)} kr`;
    },
    
    getMarginClass(margin) {
      if (margin >= 50) return 'text-green-600';
      if (margin >= 40) return 'text-yellow-600';
      return 'text-red-600';
    },
    
    getTBBackgroundClass(margin) {
      if (margin >= 50) return 'bg-green-500';
      if (margin >= 40) return 'bg-yellow-500';
      return 'bg-red-500';
    },
    
    getTBSubtextClass(margin) {
      if (margin >= 50) return 'text-green-100';
      if (margin >= 40) return 'text-yellow-100';
      return 'text-red-100';
    },

    // ---- Export Offer Methods ----
    async exportSelectedOffer() {
      if (!this.selectedOffer || this.exportingPdf) return;
      
      const offer = this.sellerOffers.find(o => o._id === this.selectedOffer);
      if (!offer) {
        console.error('Offer not found');
        return;
      }

      if (!offer.customer_name) {
        alert(this.$t('sales_dashboard.export_error'));
        return;
      }

      this.exportingPdf = true;
      try {
        await this.generateOfferPdf(offer.customer_name, offer.department_name || '');
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert(this.$t('sales_dashboard.export_error'));
      } finally {
        this.exportingPdf = false;
      }
    },

    async generateOfferPdf(companyName, departmentName) {
      const { PDFDocument, rgb, StandardFonts } = await import('pdf-lib');

      // Load the clean template PDF
      const templateUrl = `${window.location.origin}/templates/offert_template.pdf`;
      const templateBytes = await fetch(templateUrl).then(res => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(templateBytes);

      // Embed fonts
      const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      const pages = pdfDoc.getPages();
      const white = rgb(1, 1, 1);

      // Display name: "Company - Department" or just "Company"
      const displayName = departmentName
        ? `${companyName} - ${departmentName}`
        : companyName;

      // === PAGE 1 (Cover) - Company name ===
      const page1 = pages[0];
      page1.drawText(displayName, {
        x: 53.6,
        y: 223.5,
        size: 22,
        font: helvetica,
        color: white,
      });

      // === PAGE 8 (Commercial terms + Product table) ===
      const page8 = pages[7];

      // Avtalstid value (after the colon at x=536.5, y=102.5)
      const contractText = `${this.contractPeriod} ${this.$t('sales_dashboard.months')}`;
      page8.drawText(contractText, {
        x: 540,
        y: 102.5,
        size: 14,
        font: helvetica,
        color: white,
      });

      // Product table - starts below "Produktsammanställning och pris:" heading
      // Heading is at y=444.3, so table starts below
      const tableStartY = 420;
      const lineHeight = 16.8;
      const col1X = 473; // Product name
      const col2X = 720; // Quantity
      const col3X = 800; // Price
      const fontSize = 11;
      const endLineX = 850;

      // Table header
      page8.drawText(this.$t('sales_dashboard.product_col') || 'Produkt', {
        x: col1X,
        y: tableStartY,
        size: fontSize,
        font: helveticaBold,
        color: white,
      });
      page8.drawText(this.$t('sales_dashboard.quantity_col') || 'Antal', {
        x: col2X,
        y: tableStartY,
        size: fontSize,
        font: helveticaBold,
        color: white,
      });
      // Use "Pris" for cash purchases, "Pris/mån" for rentals
      const priceHeader = this.isPureCashPurchase 
        ? (this.$t('sales_dashboard.price_col_cash') || 'Pris')
        : (this.$t('sales_dashboard.price_col') || 'Pris/mån');
      page8.drawText(priceHeader, {
        x: col3X,
        y: tableStartY,
        size: fontSize,
        font: helveticaBold,
        color: white,
      });

      // Draw a thin separator line
      let currentY = tableStartY - 5;
      page8.drawLine({
        start: { x: col1X, y: currentY },
        end: { x: endLineX, y: currentY },
        thickness: 0.5,
        color: rgb(1, 1, 1),
        opacity: 0.5,
      });
      currentY -= lineHeight;

      // Product rows - rental products
      const rentalProducts = this.calculatorProducts.filter(p => !p.isCashPurchase);
      const cashProducts = this.calculatorProducts.filter(p => p.isCashPurchase);

      for (const product of rentalProducts) {
        if (currentY < 200) break; // Don't overflow page
        const name = product.name.length > 40 ? product.name.substring(0, 37) + '...' : product.name;
        page8.drawText(name, {
          x: col1X, y: currentY, size: fontSize, font: helvetica, color: white,
        });
        page8.drawText(String(product.quantity), {
          x: col2X, y: currentY, size: fontSize, font: helvetica, color: white,
        });
        const rentalPriceLabel = product.quantity > 1
          ? `${product.salePrice} kr /st`
          : `${product.salePrice} kr /st`;
        page8.drawText(rentalPriceLabel, {
          x: col3X, y: currentY, size: fontSize, font: helvetica, color: white,
        });
        currentY -= lineHeight;
      }

      // Cash products section
      if (cashProducts.length > 0) {
        currentY -= 5;
        page8.drawText(this.$t('sales_dashboard.cash_purchase_section') || 'Kontantköp', {
          x: col1X, y: currentY, size: fontSize, font: helveticaBold, color: white,
        });
        currentY -= lineHeight;

        for (const product of cashProducts) {
          if (currentY < 200) break;
          const name = product.name.length > 40 ? product.name.substring(0, 37) + '...' : product.name;
          page8.drawText(name, {
            x: col1X, y: currentY, size: fontSize, font: helvetica, color: white,
          });
          page8.drawText(String(product.quantity), {
            x: col2X, y: currentY, size: fontSize, font: helvetica, color: white,
          });
          const cashPriceLabel = product.quantity > 1
            ? `${product.salePrice} kr /st`
            : `${product.salePrice} kr /st`;
          page8.drawText(cashPriceLabel, {
            x: col3X, y: currentY, size: fontSize, font: helvetica, color: white,
          });
          currentY -= lineHeight;
        }
      }

      // Total cost - monthly for rentals, one-time for cash purchases
      currentY -= 5;
      page8.drawLine({
        start: { x: col1X, y: currentY + 14 },
        end: { x: endLineX, y: currentY + 14 },
        thickness: 1,
        color: rgb(1, 1, 1),
        opacity: 0.7,
      });
      
      // Calculate appropriate total based on purchase type
      let totalLabel, totalAmount;
      if (this.isPureCashPurchase) {
        // For pure cash purchases, show total one-time cost
        totalLabel = this.$t('sales_dashboard.total') || 'Total';
        totalAmount = this.calculatorProducts.reduce((sum, p) => sum + (p.quantity * p.salePrice), 0);
      } else {
        // For rentals or mixed, show monthly total
        totalLabel = this.$t('sales_dashboard.total_monthly') || 'Total per månad';
        totalAmount = this.monthlyRevenue;
      }
      
      page8.drawText(totalLabel, {
        x: col1X, y: currentY, size: fontSize, font: helveticaBold, color: white,
      });
      page8.drawText(`${this.formatNumber(totalAmount)} kr`, {
        x: col3X, y: currentY, size: fontSize, font: helveticaBold, color: white,
      });

      // === PAGE 12 (Back Cover) - Company name ===
      const page12 = pages[11];
      page12.drawText(displayName, {
        x: 53.6,
        y: 223.5,
        size: 22,
        font: helvetica,
        color: white,
      });

      // Generate and download
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = companyName.replace(/[^a-zA-Z0-9åäöÅÄÖ\s-]/g, '').trim();
      link.download = `Offert_${safeName}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
  
};

</script>

<style scoped>
.sd-select {
  padding: 0.5rem 2.5rem 0.5rem 0.75rem;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  appearance: none;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
}

.sd-select:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 91, 165, 0.2);
  border-color: #005ba5;
}

.sd-select-sm {
  padding: 0.375rem 2rem 0.375rem 0.5rem;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  line-height: 1.25rem;
  appearance: none;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.375rem center;
  background-repeat: no-repeat;
  background-size: 1.25em 1.25em;
}

.sd-select-sm:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 91, 165, 0.2);
  border-color: #005ba5;
}

/* XL screens (1280px - 1535px): two-row layout */
/* Row 1: Settings + Product Selection + Products List */
/* Row 2: Results + Commission */
@media (min-width: 1280px) and (max-width: 1535px) {
  .calc-panel-settings {
    flex: 0 0 17%;
  }
  .calc-panel-product-select {
    flex: 0 0 24%;
  }
  .calc-panel-product-list {
    flex: 0 0 55%;
  }
  .calc-panel-results {
    flex: 0 0 58%;
  }
  .calc-panel-commission {
    flex: 0 0 39%;
  }

  /* Offers tab: no commission panel, redistribute space */
  .calc-grid-offers .calc-panel-settings {
    flex: 0 0 20%;
  }
  .calc-grid-offers .calc-panel-product-select {
    flex: 0 0 28%;
  }
  .calc-grid-offers .calc-panel-product-list {
    flex: 0 0 48%;
  }
  .calc-grid-offers .calc-panel-results {
    flex: 0 0 97%;
  }
}

/* 2XL+ screens (1536px+): single-row layout */
@media (min-width: 1536px) {
  .calc-panel-settings {
    flex: 0 0 12.5%;
  }
  .calc-panel-product-select {
    flex: 0 0 15%;
  }
  .calc-panel-product-list {
    flex: 0 0 30.5%;
  }
  .calc-panel-results {
    flex: 0 0 22.5%;
  }
  .calc-panel-commission {
    flex: 0 0 16%;
  }

  /* Offers tab: no commission panel, redistribute space */
  .calc-grid-offers .calc-panel-settings {
    flex: 0 0 14%;
  }
  .calc-grid-offers .calc-panel-product-select {
    flex: 0 0 18%;
  }
  .calc-grid-offers .calc-panel-product-list {
    flex: 0 0 38%;
  }
  .calc-grid-offers .calc-panel-results {
    flex: 0 0 27%;
  }
}
</style>
